/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LmsShell, lmsApi, adminApi, getStudentToken, clearStudentSession } from "./shell";
import { PracticeBanner } from "./PracticeBanner";
import { ReadAloud, stripMarkdownForTts } from "./ReadAloud";
import { ArrowLeft, Check, PlayCircle, LogOut, BookOpen, ClipboardCheck } from "lucide-react";

/**
 * Lesson viewer for a single module.
 * URL: /dashboard/modules/:moduleSlug
 * Shows module summary, learning objectives, lesson list (or placeholder if empty),
 * "Mark complete" per lesson, and a "Start module quiz" button.
 */
export default function LessonViewer() {
  const { moduleSlug } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState("");
  const [busyLesson, setBusyLesson] = useState(null);

  useEffect(() => {
    if (!getStudentToken()) { nav("/login", { replace: true }); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    try {
      const p = await lmsApi.progress();
      setProgress(p[0] || null);
      const cid = (p && p[0] && p[0].course_id) || "advanced-rpas-2026";
      const c = await lmsApi.courseFull(cid);
      setCourse(c);
    } catch (e) {
      if (e.status === 401) { clearStudentSession(); nav("/login", { replace: true }); return; }
      setErr(e.message || "Load failed");
    }
  };

  const currentModule = useMemo(() => {
    if (!course) return null;
    return course.modules.find((m) => m.slug === moduleSlug) || null;
  }, [course, moduleSlug]);

  const signOut = () => { clearStudentSession(); nav("/login", { replace: true }); };
  const completed = new Set(progress?.completed_lesson_ids || []);

  const markComplete = async (lesson) => {
    setBusyLesson(lesson.id);
    try {
      const r = await lmsApi.completeLesson(lesson.id);
      setProgress((p) => ({ ...(p || {}), completed_lesson_ids: r.completed_lesson_ids }));
    } catch (e) { setErr(e.message || "Failed"); }
    finally { setBusyLesson(null); }
  };

  const rightSlot = (
    <button onClick={signOut} className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="lesson-signout">
      <LogOut size={12} /> Sign out
    </button>
  );

  if (!course) {
    return (
      <LmsShell subtitle="Loading" rightSlot={rightSlot} wide>
        <div className="tbg-lms-wide">
          <PracticeBanner />
          <div className="tbg-panel">Loading course…</div>
        </div>
      </LmsShell>
    );
  }
  if (!currentModule) {
    return (
      <LmsShell subtitle="Module not found" rightSlot={rightSlot} wide>
        <div className="tbg-lms-wide">
          <PracticeBanner />
          <div className="tbg-panel">
            <p>Module not found.</p>
            <Link to="/dashboard" className="tbg-btn" style={{ marginTop: 12 }}>Back to dashboard</Link>
          </div>
        </div>
      </LmsShell>
    );
  }

  const isBonus = !!currentModule.is_bonus;
  const lessons = (currentModule.lessons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const modQuiz = progress?.quiz_scores?.[currentModule.id];

  return (
    <LmsShell subtitle={`Module ${currentModule.code}`} rightSlot={rightSlot} wide>
      <div className="tbg-lms-wide" data-testid="lesson-viewer">
        <PracticeBanner />

        <div style={{ marginBottom: 14 }}>
          <Link to="/dashboard" className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="lesson-back">
            <ArrowLeft size={12} /> Back to dashboard
          </Link>
        </div>

        <div className="tbg-panel" style={{ marginBottom: 16 }}>
          <div className="tbg-section-sub">Module {currentModule.code}{isBonus && " · Bonus"}</div>
          <h1 className="tbg-section-title">{currentModule.title}</h1>
          {currentModule.summary && (
            <p style={{ color: "#a5b8b3", lineHeight: 1.6, margin: "6px 0 0", fontSize: 13 }}>
              {currentModule.summary}
            </p>
          )}
          {currentModule.tc_references && currentModule.tc_references.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {currentModule.tc_references.map((r) => (
                <span key={r} style={{ font: "600 10px 'JetBrains Mono', monospace",
                  color: "#3dc0af", padding: "3px 8px", border: "1px solid #2a403c",
                  borderRadius: 3, letterSpacing: "0.14em" }}>
                  {r}
                </span>
              ))}
            </div>
          )}

          {currentModule.learning_objectives && currentModule.learning_objectives.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div className="tbg-section-sub" style={{ marginBottom: 8 }}>Learning objectives</div>
              <ul style={{ color: "#cfdad7", paddingLeft: 20, lineHeight: 1.6, fontSize: 13, margin: 0 }}>
                {currentModule.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Lessons */}
        <div style={{ marginTop: 22 }}>
          <div className="tbg-section-sub">Lessons</div>
          {lessons.length === 0 ? (
            <div className="tbg-panel" data-testid="lessons-empty">
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#f0be3c" }}>
                <BookOpen size={16} />
                <strong style={{ letterSpacing: "0.1em", fontSize: 12, textTransform: "uppercase" }}>Content pending</strong>
              </div>
              <p style={{ color: "#a5b8b3", fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>
                Lessons for this module are being added by the instructor. You can still
                take the module quiz below — sample placeholder questions are available
                so you can preview the assessment flow.
              </p>
            </div>
          ) : (
            <div className="tbg-modlist">
              {lessons.map((l, idx) => {
                const done = completed.has(l.id);
                return (
                  <div key={l.id} className="tbg-qcard" data-testid={`lesson-${idx + 1}`}>
                    <div className="tbg-lesson-row">
                      <div className="lesson-body-col">
                        <div className="tbg-qnum">Lesson {idx + 1}{l.est_minutes ? ` · ${l.est_minutes} min` : ""}</div>
                        <h3 style={{ margin: "0 0 10px", color: "#e2ece9", fontSize: 15 }}>{l.title}</h3>
                        {l.body_md && (
                          <ReadAloud
                            text={`${l.title}. ${stripMarkdownForTts(l.body_md)}`}
                            testIdSuffix={`-${idx + 1}`}
                          />
                        )}
                        {l.body_md && <div className="tbg-lesson-body" style={{ marginTop: 10 }}>{renderMarkdown(l.body_md, l.id)}</div>}
                        {l.references && l.references.length > 0 && (
                          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {l.references.map((r, i) => (
                              <span key={i} style={{ font: "500 10px 'JetBrains Mono', monospace",
                                color: "#7b8b87", padding: "2px 6px", border: "1px solid #1c2a27",
                                borderRadius: 3 }}>{r}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="lesson-cta-col">
                        <button
                          className={`tbg-btn ${done ? "ghost" : ""}`}
                          onClick={() => !done && markComplete(l)}
                          disabled={done || busyLesson === l.id}
                          data-testid={`lesson-complete-${idx + 1}`}
                        >
                          {done ? (<><Check size={14} /> Completed</>) : (busyLesson === l.id ? "Saving…" : "Mark complete")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quiz launcher */}
        {!isBonus && (
          <div className="tbg-panel" style={{ marginTop: 22 }} data-testid="module-quiz-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div className="tbg-section-sub">Module Quiz</div>
                <h2 className="tbg-section-title">Check your knowledge</h2>
                <p style={{ color: "#a5b8b3", fontSize: 13, marginTop: 6, lineHeight: 1.55, maxWidth: 520 }}>
                  {currentModule.quiz_question_count || 10} multiple-choice questions ·
                  Pass mark: {currentModule.quiz_pass_pct || 70}% ·
                  Unlimited retakes. Immediate feedback with rationale on each question.
                </p>
                {modQuiz && (
                  <div style={{ marginTop: 10, font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.06em" }}>
                    Best score: <span style={{ color: modQuiz.best_pct >= (currentModule.quiz_pass_pct || 70) ? "#3dc0af" : "#f0be3c" }}>{modQuiz.best_pct}%</span>
                    {" · "}Attempts: {modQuiz.attempts}
                  </div>
                )}
              </div>
              <Link to={`/dashboard/modules/${currentModule.slug}/quiz`} className="tbg-btn" data-testid="start-module-quiz">
                <PlayCircle size={14} /> Start quiz
              </Link>
            </div>
          </div>
        )}

        {isBonus && (
          <div className="tbg-panel" style={{ marginTop: 22, borderColor: "rgba(240,190,60,0.35)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#f0be3c" }}>
              <ClipboardCheck size={16} />
              <strong style={{ letterSpacing: "0.1em", fontSize: 12, textTransform: "uppercase" }}>Bonus module — no quiz</strong>
            </div>
            <p style={{ color: "#a5b8b3", fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>
              Flight Review Prep is preparation for the in-person Flight Review with a
              Transport Canada-approved Flight Reviewer. It doesn't have a module quiz —
              the "assessment" is your real Flight Review.
            </p>
          </div>
        )}

        {err && <div className="tbg-lms-err" style={{ marginTop: 12 }}>{err}</div>}
      </div>
    </LmsShell>
  );
}

/* Minimal Markdown renderer (safe, no HTML). Handles paragraphs, bold, italics,
   inline code, links, unordered/ordered lists, h1-h3, and inline images via
   ![alt](url) — where `url` may be `asset:<slug>` (resolved against this
   lesson's asset store) or a full URL. */
function renderMarkdown(md, lessonId) {
  if (!md) return null;
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let listBuf = null; // { type: "ul"|"ol", items: [] }
  const flushList = () => {
    if (!listBuf) return;
    const Tag = listBuf.type;
    out.push(<Tag key={`l-${out.length}`}>{listBuf.items.map((it, i) => <li key={i}>{inline(it, lessonId)}</li>)}</Tag>);
    listBuf = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushList(); continue; }
    let m;
    if ((m = line.match(/^###\s+(.*)$/))) { flushList(); out.push(<h3 key={out.length}>{inline(m[1], lessonId)}</h3>); continue; }
    if ((m = line.match(/^##\s+(.*)$/))) { flushList(); out.push(<h2 key={out.length}>{inline(m[1], lessonId)}</h2>); continue; }
    if ((m = line.match(/^#\s+(.*)$/))) { flushList(); out.push(<h1 key={out.length}>{inline(m[1], lessonId)}</h1>); continue; }
    if ((m = line.match(/^\s*[-*]\s+(.*)$/))) {
      if (!listBuf || listBuf.type !== "ul") { flushList(); listBuf = { type: "ul", items: [] }; }
      listBuf.items.push(m[1]); continue;
    }
    if ((m = line.match(/^\s*\d+\.\s+(.*)$/))) {
      if (!listBuf || listBuf.type !== "ol") { flushList(); listBuf = { type: "ol", items: [] }; }
      listBuf.items.push(m[1]); continue;
    }
    flushList();
    // Image-only line → emit <figure> at block level (avoids invalid
    // <figure> inside <p>). Anything richer still goes through <p>.
    const imgOnly = line.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (imgOnly) {
      const alt = imgOnly[1];
      const src = resolveAssetUrl(imgOnly[2].trim(), lessonId);
      if (src) {
        out.push(
          <figure key={out.length} className="tbg-lesson-figure">
            <img src={src} alt={alt} loading="lazy" />
            {alt && <figcaption>{alt}</figcaption>}
          </figure>,
        );
      } else {
        out.push(<p key={out.length}>{alt}</p>);
      }
      continue;
    }
    out.push(<p key={out.length}>{inline(line, lessonId)}</p>);
  }
  flushList();
  return out;
}

function resolveAssetUrl(url, lessonId) {
  if (!url) return null;
  if (url.startsWith("asset:")) {
    if (!lessonId) return null; // asset ref without lesson context — render alt only
    return adminApi.lessonAssetUrl(lessonId, url.slice("asset:".length));
  }
  // Only allow http(s) and same-origin API paths — protect against javascript: URIs
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("/")) return url;
  return null;
}

function inline(str, lessonId) {
  // Bold / italic / inline code / links / images. Escape everything else literally.
  const parts = [];
  let i = 0;
  const push = (s) => { if (s) parts.push(s); };
  while (i < str.length) {
    // inline image ![alt](url) — emit a bare <img> (no figure/caption at
    // inline scope; block-level figures are handled by renderMarkdown for
    // image-only lines). Must be checked before link so it doesn't misread.
    if (str[i] === "!" && str[i + 1] === "[") {
      const img = str.slice(i).match(/^!\[([^\]]*)\]\(([^)]+)\)/);
      if (img) {
        const alt = img[1];
        const src = resolveAssetUrl(img[2].trim(), lessonId);
        if (src) {
          parts.push(
            <img key={parts.length} className="tbg-lesson-inline-img"
              src={src} alt={alt} loading="lazy" />,
          );
        } else {
          // Unsafe/unknown URL scheme — render alt text only.
          push(alt);
        }
        i += img[0].length; continue;
      }
    }
    // link [text](url)
    const link = str.slice(i).match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (link) {
      parts.push(<a key={parts.length} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>);
      i += link[0].length; continue;
    }
    if (str[i] === "*" && str[i + 1] === "*") {
      const end = str.indexOf("**", i + 2);
      if (end > 0) { parts.push(<strong key={parts.length}>{str.slice(i + 2, end)}</strong>); i = end + 2; continue; }
    }
    if (str[i] === "*") {
      const end = str.indexOf("*", i + 1);
      if (end > 0) { parts.push(<em key={parts.length}>{str.slice(i + 1, end)}</em>); i = end + 1; continue; }
    }
    if (str[i] === "`") {
      const end = str.indexOf("`", i + 1);
      if (end > 0) { parts.push(<code key={parts.length}>{str.slice(i + 1, end)}</code>); i = end + 1; continue; }
    }
    // consume until next special
    const next = str.slice(i).search(/[!*`[]/);
    if (next < 0) { push(str.slice(i)); break; }
    // Feb 2026 bug fix: when `next === 0`, the current char is a special
    // one (`!`, `*`, `` ` ``, or `[`) that none of the parsers above could
    // consume — e.g. a bare `[C of G]` bracket not followed by `(url)`,
    // an unclosed `**bold`, an unbalanced backtick, or a `!` not followed
    // by `[`. Before this guard, `i += 0` on those chars locked the
    // browser tab into an infinite loop (any module whose lesson body
    // used bracket citations without link URLs — e.g. "[C of G]",
    // "[Quick Check]" — would hang the entire page). Push the char
    // literally and advance so the parser stays forward-progressing.
    if (next === 0) { push(str[i]); i += 1; continue; }
    push(str.slice(i, i + next)); i += next;
  }
  return parts;
}
